'use client';

import { useEffect } from 'react';
import { isDACHLU, getCountryByLocale, getJentisUserId } from 'utils';
import { type AuthViewModeType, AUTH_VIEW_MODE } from '@pixum/auth-ui';
import { trackCustomerScore, trackAuthFormOnCustomerScore } from '../../analytics/auth';
import {
  CUSTOMER_SCORE_THRESHOLD,
  getStoredCustomerScore,
  fetchAndStoreCustomerScore
} from '../../utils/auth/customerScore';

interface UseCustomerScoreParams {
  locale: string;
  isAuthenticated: boolean;
  initialView: AuthViewModeType;
  onViewRecommended: (view: AuthViewModeType) => void;
}

/**
 * Fetches the Pixum ML "neukunde" customer score for DACHLU unauthenticated users
 * and pre-selects the Register or Login tab accordingly.
 *
 * Behaviour mirrors the Gatsby CartAuth shouldGetCustomerScore useEffect:
 * 1. Reads any cached score from localStorage immediately — applies view switch synchronously.
 * 2. Fires a fresh fetch to the ML API (fire-and-forget) to update the cache for future visits.
 *    If no cached score existed, the view is switched once the fresh score arrives.
 * 3. Skips when locale is not DACHLU or the user is already authenticated.
 * 4. `onViewRecommended` is only called when the recommended view differs from `initialView`
 *    to avoid unnecessary AuthFlow remounts.
 */
export function useCustomerScore({
  locale,
  isAuthenticated,
  initialView,
  onViewRecommended
}: UseCustomerScoreParams): void {
  useEffect(() => {
    const country = getCountryByLocale(locale).countryId;
    const isDachlu = isDACHLU(country);

    if (!isDachlu || isAuthenticated) return;

    const toView = (score: number): AuthViewModeType =>
      score > CUSTOMER_SCORE_THRESHOLD ? AUTH_VIEW_MODE.REGISTER : AUTH_VIEW_MODE.LOGIN;

    // Switches view and tracks. Used when a real score drives the recommendation.
    // Only switches when starting from the default login view — explicit register/
    // forgot-password navigations (URL or tab toggle) must not be overridden by ML score.
    const applyView = (view: AuthViewModeType): void => {
      if (view !== initialView && initialView === AUTH_VIEW_MODE.LOGIN) {
        onViewRecommended(view);
      }

      trackAuthFormOnCustomerScore(view);
    };

    const cachedScore = getStoredCustomerScore();

    if (cachedScore !== null) {
      // A real score exists — switch view if needed and track.
      applyView(toView(cachedScore));
    } else {
      // No cached score: always track the default (LOGIN) but do NOT switch view.
      // Mirrors Gatsby's toggleAuthFormOnCustomerScore which fires regardless of cache.
      // Without a score, the LOGIN default must not override an intentional /register navigation.
      trackAuthFormOnCustomerScore(AUTH_VIEW_MODE.LOGIN);
    }

    // Refresh score for future visits. If no cache existed, switch view when fresh score arrives.
    const jentisUserId = getJentisUserId();

    if (!jentisUserId) return;

    fetchAndStoreCustomerScore(jentisUserId).then(freshScore => {
      if (freshScore === null) return;

      trackCustomerScore(freshScore.toString());

      if (cachedScore === null) {
        applyView(toView(freshScore));
      }
    });
    // Intentionally empty: runs once on mount. locale and isAuthenticated are stable server-rendered values.
  }, []);
}
